import type { CommentAnchorType } from "../../../services/types";
import type { CommentNode, CommentSiblingInfo } from "../../comments/explorer/types";
import { getCommentPath, getRootForComment } from "../../comments/explorer/commentTreeModel";

export function getFirstDiscussionCommentId(roots: CommentNode[]): number | null {
  return roots[0]?.id ?? null;
}

export function getSafeSelectedCommentId(
  byId: Map<number, CommentNode>,
  roots: CommentNode[],
  selectedCommentId?: number | null,
): number | null {
  if (typeof selectedCommentId === "number" && byId.has(selectedCommentId)) {
    return selectedCommentId;
  }

  return getFirstDiscussionCommentId(roots);
}

export function getSiblingsForComment(
  roots: CommentNode[],
  byId: Map<number, CommentNode>,
  comment: CommentNode | null,
): CommentNode[] {
  if (!comment) return roots;
  if (typeof comment.parentId === "number") {
    return byId.get(comment.parentId)?.replies ?? [];
  }
  return roots;
}

export function getPathForComment(
  byId: Map<number, CommentNode>,
  comment: CommentNode | null,
): CommentNode[] {
  if (!comment) return [];
  return getCommentPath(byId, comment.id);
}

export function getRootForSelectedComment(
  byId: Map<number, CommentNode>,
  comment: CommentNode | null,
): CommentNode | null {
  return getRootForComment(byId, comment);
}


export function getAnchorForSelectedComment(
  byId: Map<number, CommentNode>,
  anchorsByRootCommentId: Map<number, CommentAnchorType>,
  comment: CommentNode | null,
): CommentAnchorType | null {
  const root = getRootForComment(byId, comment);
  if (!root) return null;
  return anchorsByRootCommentId.get(root.id) ?? null;
}

export function getParentForComment(
  byId: Map<number, CommentNode>,
  comment: CommentNode | null,
): CommentNode | null {
  if (!comment || typeof comment.parentId !== "number") return null;
  return byId.get(comment.parentId) ?? null;
}

export function getSiblingInfoForComment(
  roots: CommentNode[],
  byId: Map<number, CommentNode>,
  comment: CommentNode | null,
): CommentSiblingInfo {
  if (!comment) return { index: 0, count: 0 };

  const siblings = getSiblingsForComment(roots, byId, comment);
  return {
    index: Math.max(0, siblings.findIndex((candidate) => candidate.id === comment.id)),
    count: siblings.length,
  };
}

export function getPreviousSibling(
  roots: CommentNode[],
  byId: Map<number, CommentNode>,
  comment: CommentNode | null,
): CommentNode | null {
  if (!comment) return null;
  const siblings = getSiblingsForComment(roots, byId, comment);
  const index = siblings.findIndex((candidate) => candidate.id === comment.id);
  if (index <= 0) return null;
  return siblings[index - 1] ?? null;
}

export function getNextSibling(
  roots: CommentNode[],
  byId: Map<number, CommentNode>,
  comment: CommentNode | null,
): CommentNode | null {
  if (!comment) return null;
  const siblings = getSiblingsForComment(roots, byId, comment);
  const index = siblings.findIndex((candidate) => candidate.id === comment.id);
  if (index < 0 || index >= siblings.length - 1) return null;
  return siblings[index + 1] ?? null;
}

export function getFirstChild(comment: CommentNode | null): CommentNode | null {
  return comment?.replies[0] ?? null;
}

export function getBranchDepthLabel(comment: CommentNode | null): string {
  if (!comment) return "No selection";
  if (comment.depth === 0) return "Root thread";
  if (comment.depth === 1) return "First-level reply";
  return `Depth ${comment.depth} reply`;
}
